// AddNewWhitelistRuleDialog.tsx
import React, { useState, useEffect, useRef } from "react";
import { AddNewWhitelistRuleProps } from "../../Interfaces/interfaces";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { apiService } from "../../../../api/api";
import { WhitelistRule } from "../../../../Interfaces/interfaces";
import {
  auditOptions,
  conditionOptions,
  fieldsOptions,
  propertyOptions,
} from "../../Objects/multiSelectObjects";
import { useSelector } from "react-redux";

const AddNewWhitelistRuleDialog: React.FC<AddNewWhitelistRuleProps> = ({
  rowData,
  visible,
  onHide,
  onSave, // Correct prop usage - include tableType
}) => {
  const [whitelistRule, setWhitelistRule] = useState<WhitelistRule | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const user = useSelector((state: any) => state.googleAuth.user);
  const loggedInUserEmail = user.displayName;

  const formatDateTime = () => {
    const date = new Date();
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .format(date)
      .replace(",", "");
  };

  useEffect(() => {
    if (visible) {
      // Initialize ONLY when dialog is visible
      setWhitelistRule({
        property: "",
        condition: "",
        value: "",
        audit: "",
        fields: "",
        notes: "",
        user: loggedInUserEmail,
        lastInit: formatDateTime(),
        dateAdded: formatDateTime(),
      });
    } else {
      setWhitelistRule(null); // Reset state when dialog is closed.  This is crucial!
    }
  }, [loggedInUserEmail, rowData, visible]); // Correct dependencies

  // Example with correct state updates:
  const handlePropertyChange = (e: any) => {
    //Correct type and state update
    setWhitelistRule((prev) => (prev ? { ...prev, property: e.value } : null)); // Correctly handle prevState
  };

  //Same implementation as above function, just different prop names.
  const handleConditionChange = (e: any) => {
    setWhitelistRule((prev) =>
      prev ? { ...prev, condition: e.value, value: "" } : null
    );
  };

  const handleValueChange = (value: string) => {
    //Correct type and state update
    setWhitelistRule((prev) => (prev ? { ...prev, value } : null));
  };

  const handleAuditChange = (e: any) => {
    //Correct type and state update
    setWhitelistRule((prev) =>
      prev ? { ...prev, audit: e.value, fields: "" } : null
    ); //Also clear fields when audit changes
  };

  const handleFieldsChange = (e: any) => {
    //Correct type and state update
    setWhitelistRule((prev) => (prev ? { ...prev, fields: e.value } : null));
  };

  const handleNotesChange = (e: any) => {
    //Correct type and state update
    setWhitelistRule((prev) =>
      prev ? { ...prev, notes: e.target.value } : null
    );
  };

  const handleSaveWhitelistRule = async () => {
    setLoading(true);

    try {
      if (
        !whitelistRule ||
        !whitelistRule.property ||
        !whitelistRule.condition ||
        !whitelistRule.value ||
        !whitelistRule.audit ||
        whitelistRule.fields.length === 0
      ) {
        toast.current?.show({
          severity: "warn",
          summary: "Warning",
          detail: "Please fill all the required fields.",
          life: 3000,
        }); // Show warning toast
        setLoading(false);
        return; // Stop further execution
      }

      const ruleToSave = { ...whitelistRule };

      console.log("Request body:", ruleToSave);

      await apiService.youtubeScorecard.addWhitelistRule(ruleToSave);
      await apiService.youtubeScorecard.getYouTubeScorecardWhitelist();

      onSave?.(ruleToSave); // Pass the updated rule back to the parent
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Whitelist rule saved",
        life: 3000,
      });
    } catch (error: any) {
      console.error("Error saving whitelist rule:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 3000,
      });
    } finally {
      setLoading(false);
      onHide();
    }
  };

  return (
    <Dialog
      header={`Add New Whitelist Rule`}
      visible={visible}
      onHide={onHide}
      footer={
        <div>
          {/* Ensure consistent disabled state for the Save button */}
          <Button
            label="Save"
            onClick={handleSaveWhitelistRule}
            loading={loading}
            disabled={
              !whitelistRule ||
              !whitelistRule.property ||
              !whitelistRule.condition ||
              !whitelistRule.value ||
              !whitelistRule.audit ||
              whitelistRule.fields.length === 0
            }
          />
          <Button label="Cancel" onClick={onHide} className="ml-2" />
        </div>
      }
      style={{ width: "50%" }}
    >
      {/* Form Fields */}
      <div className="p-fluid p-formgrid p-grid">
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="property">Property</label>
          <Dropdown
            inputId="property"
            value={whitelistRule?.property}
            options={propertyOptions}
            onChange={handlePropertyChange}
            optionLabel="label"
            placeholder="Select Property"
          />
        </div>

        <div className="p-field p-col-12 p-md-4">
          {" "}
          {/* Condition */}
          <label htmlFor="condition">Condition</label>
          <Dropdown
            inputId="condition" // Add inputId
            value={whitelistRule?.condition}
            options={conditionOptions}
            onChange={handleConditionChange}
            optionLabel="label"
            placeholder="Select Condition"
          />
        </div>

        {/* Value Input */}
        <div className="p-field p-col-12 p-md-4">
          <label htmlFor="value">Value</label>
          <InputText // Or MultiSearch, as needed
            value={whitelistRule?.value || ""}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder="Enter value"
          />
        </div>

        {/* Audit Dropdown */}
        <div className="p-field p-col-12 p-md-6">
          <label htmlFor="audit">Audit</label>
          <Dropdown
            inputId="audit" // Add inputId
            value={whitelistRule?.audit}
            options={auditOptions}
            onChange={handleAuditChange}
            optionLabel="label"
            placeholder="Select Audit"
          />
        </div>

        <div className="p-field p-col-12 p-md-6">
          {" "}
          {/* Fields */}
          <label htmlFor="fields">Fields</label>
          <MultiSelect
            inputId="fields" // Add inputId
            value={whitelistRule?.fields}
            options={
              whitelistRule?.audit ? fieldsOptions[whitelistRule?.audit] : []
            } //Show options based on the chosen Audit
            onChange={handleFieldsChange}
            optionLabel="label"
            placeholder="Select Fields"
            filter
          />
        </div>

        <div className="p-field p-col-12">
          {" "}
          {/* Notes */}
          <label htmlFor="notes">Notes</label>
          <InputTextarea
            value={whitelistRule?.notes}
            onChange={handleNotesChange}
            rows={3}
            cols={20}
          />
        </div>
      </div>

      <Toast ref={toast} />
    </Dialog>
  );
};

export default AddNewWhitelistRuleDialog;
